import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import Logo from "./Logo";

const NAV = [
  { label: "Work", to: "/ourwork" },
  { label: "Gallery", to: "/gallery" },
  { label: "About", to: "/about" },
  { label: "Team", to: "/team" },
  { label: "Contact", to: "/map" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile nav on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Lock body scroll when menu open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-500 ${
          scrolled
            ? "py-3 backdrop-blur-xl bg-[rgba(10,22,53,0.85)] border-b border-[var(--line)]"
            : "py-5 bg-transparent"
        }`}
      >
        <div className="px-5 md:px-10 flex items-center justify-between">
          {/* Logo — full TABA mark (marble bust + copper wordmark) */}
          <Link
            to="/"
            className="flex items-center gap-3 group relative z-[101]"
            aria-label="TABA — The Art By Artist, Home"
          >
            <Logo size="sm" className="transition-transform duration-500 group-hover:scale-105" />
            <span className="hidden sm:flex flex-col leading-tight">
              <span className="font-display font-light tracking-[0.18em] uppercase text-[var(--bone)] text-base md:text-lg">
                Taba
              </span>
              <span className="text-[9px] uppercase tracking-[0.32em] text-[var(--clay)]">
                The Art By Artist
              </span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-sm tracking-wide font-medium transition-colors ${
                    isActive
                      ? "text-[var(--bone)]"
                      : "text-[var(--muted)] hover:text-[var(--bone)]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    <span
                      className={`absolute left-1/2 -translate-x-1/2 bottom-0 h-px bg-[var(--clay)] transition-all duration-500 ${
                        isActive ? "w-6" : "w-0"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Right CTA */}
          <div className="flex items-center gap-3">
            <Link
              to="/booknow"
              className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-medium bg-[var(--clay)] text-[var(--bone)] rounded-full hover:bg-[var(--bone)] hover:text-[var(--ink)] transition-colors"
            >
              Book a Mural
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M5 12h14M13 5l7 7-7 7" />
              </svg>
            </Link>

            {/* Hamburger */}
            <button
              aria-label={open ? "Close menu" : "Open menu"}
              onClick={() => setOpen((v) => !v)}
              className="relative z-[101] lg:hidden w-10 h-10 grid place-items-center text-[var(--bone)]"
            >
              <div className="relative w-6 h-4">
                <span
                  className={`absolute left-0 right-0 h-px bg-current transition-all duration-300 ${
                    open ? "top-1/2 -translate-y-1/2 rotate-45" : "top-0"
                  }`}
                />
                <span
                  className={`absolute left-0 right-0 h-px bg-current transition-opacity duration-200 ${
                    open ? "opacity-0" : "top-1/2 -translate-y-1/2 opacity-100"
                  }`}
                />
                <span
                  className={`absolute left-0 right-0 h-px bg-current transition-all duration-300 ${
                    open ? "top-1/2 -translate-y-1/2 -rotate-45" : "bottom-0"
                  }`}
                />
              </div>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-[99] lg:hidden transition-all duration-500 ${
          open ? "visible opacity-100" : "invisible opacity-0"
        }`}
      >
        <div
          className="absolute inset-0 bg-[var(--bg)]/95 backdrop-blur-2xl"
          onClick={() => setOpen(false)}
        />
        <div
          className={`absolute inset-0 flex flex-col justify-center px-8 transition-transform duration-700 ${
            open ? "translate-y-0" : "translate-y-8"
          }`}
        >
          <div className="flex items-center justify-center mb-8">
            <Logo size="lg" />
          </div>

          <div className="flex flex-col gap-1">
            {NAV.map((item, i) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className="group flex items-baseline justify-between border-b border-[var(--line)] py-5"
              >
                <span className="flex items-baseline gap-4">
                  <span className="text-[var(--muted)] text-xs tracking-[0.3em] tabular-nums">
                    0{i + 1}
                  </span>
                  <span className="font-display text-4xl sm:text-5xl text-[var(--bone)] group-hover:text-[var(--clay)] transition-colors">
                    {item.label}
                  </span>
                </span>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  className="text-[var(--muted)] group-hover:text-[var(--clay)] -rotate-45 group-hover:rotate-0 transition-all duration-500"
                >
                  <path d="M5 12h14M13 5l7 7-7 7" />
                </svg>
              </Link>
            ))}
          </div>

          <div className="mt-12 flex flex-col gap-3 text-sm">
            <Link
              to="/booknow"
              onClick={() => setOpen(false)}
              className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-[var(--clay)] text-[var(--bone)] rounded-full"
            >
              Book a Mural
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M5 12h14M13 5l7 7-7 7" />
              </svg>
            </Link>
            <a
              href="tel:+919911499221"
              className="text-[var(--muted)] tracking-wide"
            >
              +91 99114 99221
            </a>
            <a
              href="mailto:tabaartist@gmail.com"
              className="text-[var(--muted)] tracking-wide"
            >
              tabaartist@gmail.com
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
